import React, { useState, useEffect } from 'react';
import { Table, Button, Select, DatePicker, Input, Form, Tag, message, Card } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getMyLeaves, createLeaveRecord } from '../../api/leaves';
import { apiErrorMessage } from '../../utils/apiError';

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const TYPE_LABELS = {
  dogum_izni: 'Doğum İzni',
  yillik_izin: 'Yıllık İzin',
  mazeret_izni: 'Mazeret İzni',
  yarim_gun: 'Yarım Gün',
};

const STATUS_TAGS = {
  pending: <Tag color="gold">Bekliyor</Tag>,
  approved: <Tag color="green">Onaylandı</Tag>,
  rejected: <Tag color="red">Reddedildi</Tag>,
};

const MyLeavesTab = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await getMyLeaves();
      setRecords(res.data.data || []);
    } catch {
      message.error('İzin kayıtları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRecords(); }, []);

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      await createLeaveRecord({
        type: values.type,
        start_at: values.range[0].format('YYYY-MM-DD HH:mm:ss'),
        end_at: values.range[1].format('YYYY-MM-DD HH:mm:ss'),
        note: values.note,
      });
      message.success('İzin talebiniz gönderildi.');
      form.resetFields();
      fetchRecords();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Talep gönderilemedi.'));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'Tür', dataIndex: 'type', render: (t) => TYPE_LABELS[t] || t },
    {
      title: 'Tarih Aralığı',
      render: (_, r) => `${dayjs(r.start_at).format('DD.MM.YYYY HH:mm')} — ${dayjs(r.end_at).format('DD.MM.YYYY HH:mm')}`,
    },
    { title: 'Not', dataIndex: 'note', render: (n) => n || <span className="text-gray-400">-</span> },
    { title: 'Durum', dataIndex: 'status', render: (s) => STATUS_TAGS[s] },
    {
      title: 'Karar Tarihi',
      dataIndex: 'decided_at',
      render: (d) => d ? dayjs(d).format('DD.MM.YYYY HH:mm') : <span className="text-gray-400">-</span>,
    },
  ];

  return (
    <div>
      <Card size="small" className="mb-4" title="Yeni İzin Talebi">
        <Form form={form} layout="inline" onFinish={handleSubmit}>
          <Form.Item name="type" rules={[{ required: true, message: 'Tür seçiniz.' }]}>
            <Select placeholder="Tür" style={{ width: 150 }}>
              {Object.entries(TYPE_LABELS).map(([value, label]) => (
                <Select.Option key={value} value={value}>{label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="range" rules={[{ required: true, message: 'Tarih aralığı seçiniz.' }]}>
            <RangePicker showTime format="DD.MM.YYYY HH:mm" />
          </Form.Item>
          <Form.Item name="note">
            <TextArea placeholder="Not (opsiyonel)" autoSize style={{ width: 220 }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" icon={<PlusOutlined />} htmlType="submit" loading={saving}>
              Talep Gönder
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Table columns={columns} dataSource={records} rowKey="id" loading={loading} pagination={false} />
    </div>
  );
};

export default MyLeavesTab;
